/**
 * modal-system.js
 *
 * A reusable modal system for the Invoice Generation block.
 * Supports different content types via <template> elements.
 */

export default class InvoiceModal {
	constructor(modalId = 'ig-modal') {
		this.modal = document.getElementById(modalId);
		if (!this.modal) return;

		this.contentArea = this.modal.querySelector('#ig-modal-content');
		this.closeBtn = this.modal.querySelector('.cc-modal__close-btn');
		this.overlay = this.modal.querySelector('.cc-modal__overlay');

		this.initEvents();
	}

	initEvents() {
		this.closeBtn?.addEventListener('click', () => this.close());
		this.overlay?.addEventListener('click', () => this.close());

		window.addEventListener('keydown', (e) => {
			if (e.key === 'Escape' && !this.modal.getAttribute('aria-hidden')) {
				this.close();
			}
		});
	}

	/**
	 * Open the modal with a specific template and data.
	 * 
	 * @param {string} templateId 
	 * @param {object} data 
	 */
	open(templateId, data = {}) {
		if (!this.modal || !this.contentArea) return;

		const template = document.getElementById(templateId);
		if (!template) {
			console.error(`[IG Modal] Template not found: ${templateId}`);
			return;
		}

		// Clear previous content
		this.contentArea.innerHTML = '';

		// Clone and populate
		const clone = template.content.cloneNode(true);
		this.populateFields(clone, data);

		this.contentArea.appendChild(clone);

		// Show modal
		this.modal.classList.add('is-active');
		setTimeout(() => {
			this.modal.removeAttribute('aria-hidden');
			document.body.style.overflow = 'hidden'; // Lock scroll
		}, 10);

		// Handle inner close actions (like the UŽDARYTI button in receipt preview)
		this.contentArea.querySelectorAll('.cc-modal__close-action').forEach(btn => {
			btn.addEventListener('click', () => this.close());
		});
	}

	close() {
		if (!this.modal) return;

		this.modal.setAttribute('aria-hidden', 'true');
		document.body.style.overflow = ''; // Unlock scroll

		setTimeout(() => {
			this.modal.classList.remove('is-active');
			this.contentArea.innerHTML = '';
		}, 300); // Wait for transition
	}

	/**
	 * Recursively populate fields in a fragment based on data-field and data-list attributes.
	 * 
	 * @param {DocumentFragment|HTMLElement} container 
	 * @param {object} data 
	 */
	populateFields(container, data) {
		// Single fields
		container.querySelectorAll('[data-field]').forEach(el => {
			const field = el.getAttribute('data-field');
			if (data[field] !== undefined) {
				el.textContent = data[field];
			}
		});

		// Lists (e.g. Items table)
		container.querySelectorAll('[data-list]').forEach(listEl => {
			const listKey = listEl.getAttribute('data-list');
			const items = data[listKey];

			if (Array.isArray(items)) {
				// Assumes there's a template for the row nearby or a specific one
				const rowTemplate = document.getElementById(`tmpl-ig-${listKey.toLowerCase().slice(0, -1)}-row`);
				if (rowTemplate) {
					items.forEach(itemData => {
						const rowClone = rowTemplate.content.cloneNode(true);
						this.populateFields(rowClone, itemData);
						listEl.appendChild(rowClone);
					});
				}
			}
		});
	}
}
